import { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useU3143Context } from "../U3143Context";
import { doCreateData, doDeleteData, doGetAuditsData, doGetData, doSearch, doUpdateData, emptyDistrictCadastre, districtCadastreHandleApiError } from "../U3143Service";
import type { DistrictCadastre } from "../U3143Service";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { NS, U3143ROLES } from "../U3143";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U3143DistrictCadastreFormToolBar } from "./form/u3143DistrictCadastre/U3143DistrictCadastreFormToolBar";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import U3143DistrictCadastreBody from "./form/u3143DistrictCadastre/U3143DistrictCadastreBody";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import U3143CadastreBody from "./form/u3143DistrictCadastre/u3143CadastreTab/U3143CadastreBody";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U3143DistrictCadastreDeleteDlg from "./common/U3143DistrictCadastreDeleteDlg";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U3143DistrictCadastreFormExtendedHandlers extends U2000FormStateHandlersReturn<DistrictCadastre> {
  addNewDistrictCadastre: () => void;
  modifyDistrictCadastre: (districtCadastre: DistrictCadastre) => void;
  deleteDistrictCadastre: (id: string) => Promise<void>;
}

const U3143DistrictCadastreForm: React.FC = () => {
  // Parameters
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for u3143DistrictCadastreBL
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // APA Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3143Context();

  // Form Handlers and default methods
  const validateForm = (districtCadastre: DistrictCadastre) => {
    const result: Record<string, string> = {};
    if (!districtCadastre.codeDistrict) result.codeDistrict = t("errCodeDistrictRequired", { ns: NS });
    if (!districtCadastre.propertyDistrict) {
      result.propertyDistrict = t("errPropertyDistrictRequired", { ns: NS });
    }
    return result;
  };

  const u3143DistrictCadastreBL: U2000FormSBusinessHandlers<DistrictCadastre> = {
    newRecord: () => {
      return emptyDistrictCadastre;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (districtCadastre: DistrictCadastre, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(districtCadastre);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(districtCadastre, editMode);
      } else {
        u3143DistrictCadastreformStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const districtCadastre = u3143DistrictCadastreformStateExtendedHandlers.getOldData() as DistrictCadastre | null;
      if (districtCadastre) {
        // Met à false checkedId pour chaque cadastre
        if (Array.isArray(districtCadastre.cadastres)) {
          districtCadastre.cadastres = districtCadastre.cadastres.map((cadastre) => ({ ...cadastre, idChecked: false }));
        }
        u3143DistrictCadastreformStateHandlers.setData({ ...districtCadastre });
      }

      if (luStateHandlers.getActionOriginFrom() === "/U3143") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (districtCadastre) => {
      if (districtCadastre.id) {
        handleGet.getData(districtCadastre.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U3143");
    },
  };

  const u3143DistrictCadastreformStateHandlers = useU2000FormStateHandlers<DistrictCadastre>({
    formState,
    formStateDispatch,
    businessHandlers: u3143DistrictCadastreBL,
  });

  // extended methods
  const u3143DistrictCadastreformStateExtendedHandlers: U3143DistrictCadastreFormExtendedHandlers = {
    ...u3143DistrictCadastreformStateHandlers,

    addNewDistrictCadastre: () => {
      u3143DistrictCadastreformStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },

    modifyDistrictCadastre: () => {
      u3143DistrictCadastreformStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },

    deleteDistrictCadastre: async (_id: string) => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for District Cadastre
  const handleSearch = useU2000SearchHandlers<DistrictCadastre>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<DistrictCadastre>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, districtCadastreHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for District Cadastre
  const handleGet = useU2000GetHandlers<DistrictCadastre & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (districtCadastre: DistrictCadastre & { id: string }) => {
      gridStateHandlers.setDataById(districtCadastre.id, districtCadastre);
      u3143DistrictCadastreformStateExtendedHandlers.setData(districtCadastre);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(districtCadastre.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U3143", label: t("title", { ns: NS }) }, { label: districtCadastre.propertyDistrict + " (" + districtCadastre.codeDistrict + ")" }]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3143DistrictCadastreformStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, districtCadastreHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for District Cadastre
  const handleSave = useU2000SaveHandlers<DistrictCadastre>({
    doSaveFn: (districtCadastre: DistrictCadastre, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, districtCadastre);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, districtCadastre.id, districtCadastre);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (districtCadastre: DistrictCadastre & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), districtCadastre]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(districtCadastre.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(districtCadastre.id, districtCadastre);
      }
      u3143DistrictCadastreformStateExtendedHandlers.afterSaveData(districtCadastre);
      toast.success(t("msgDistrictCadastreSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3143") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, districtCadastreHandleApiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3143DistrictCadastreformStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3143DistrictCadastreformStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for District Cadastre
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3143DistrictCadastreformStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["codeDistrict"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3143", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, districtCadastreHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U3143/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3143DistrictCadastreformStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3143DistrictCadastreformStateExtendedHandlers.setData({ ...emptyDistrictCadastre });
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabCadastres",
      label: t("tabCadastres", { ns: NS }),
      content: <U3143CadastreBody u3143DistrictCadastreformStateExtendedHandlers={u3143DistrictCadastreformStateExtendedHandlers} />,
    },
    hasRole(U3143ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u3143DistrictCadastreformStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3143DistrictCadastreAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U3143DistrictCadastreDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3143DistrictCadastreFormToolBar formStateHandlers={u3143DistrictCadastreformStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3143DistrictCadastreformStateExtendedHandlers as any}
      >
        <U3143DistrictCadastreBody formStateHandlers={u3143DistrictCadastreformStateExtendedHandlers as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab="tabCadastres"
          activeTab={activeTab ?? "tabCadastres"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3143DistrictCadastreForm;
